/**
 * @file 学员管理页 · 类型与展示工具
 * @description 展平行模型、状态文案、选项映射；选项对齐 `@/enum/common`。
 *
 * @whenToUse 仅学员管理页。
 * @whenNotToUse 其他业务模块。
 */

import type { StudentItem } from '@/api/student.ts'
import {
  educationStageMap,
  gradeMap,
  learningLevelMap,
  semesterMap,
  subjectMap,
} from '@/enum/common.ts'

/** 学员状态（对齐接口：0-禁用 1-正常 2-试学） */
export type StudentStatus = '0' | '1' | '2'

/** 状态文案 */
export const STUDENT_STATUS_LABEL: Record<StudentStatus, string> = {
  '1': '正常',
  '2': '试学',
  '0': '禁用',
}

/** 列表展平行（一科一行；学员维度字段在多行间重复，由 onCell 合并） */
export type StudentListRow = {
  /** 行 ID（科目关联记录 ID） */
  id: string
  /** 所属学员 ID */
  studentId: string
  /** 学员名称 */
  studentName: string
  /** 手机号 */
  phone: string
  /** 点数 */
  points: number
  /** 状态 */
  status: StudentStatus
  /** 允许 PAD 端调整基本信息（0-不允许 1-允许） */
  allowEditBasicInfo: number
  /** 创建时间 */
  createTime: string
  /** 科目 */
  subject: string
  /** 教材名称（服务端返回） */
  textbookName: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 教育阶段 */
  educationStage: string
  /** 学习力等级 */
  learningGrade: string
  /** 学习力值 */
  learningForce: number
  /** 所属教师名称 */
  teacherName: string
  /** 所属教师 id */
  teacherId: string
}

/** 列表筛选条件 */
export type StudentQuery = {
  /** 学员名称（模糊） */
  studentName: string
  /** 状态；空为全部 */
  status: string
  /** 创建时间区间；起止选齐才生效 */
  createTimeRange: { from?: string; to?: string }
  /** 科目 */
  subject: string
  /** 教材 id */
  textbook: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 教育阶段 */
  educationStage: string
  /** 学习力等级 */
  learningGrade: string
  /** 学习力；undefined 表示不限（等值） */
  learningForce: number | undefined
}

export const EMPTY_STUDENT_QUERY: StudentQuery = {
  studentName: '',
  status: '',
  createTimeRange: {},
  subject: '',
  textbook: '',
  grade: '',
  semester: '',
  educationStage: '',
  learningGrade: '',
  learningForce: undefined,
}

/**
 * 将枚举映射转为 Select 选项
 * @param map 枚举文案表
 */
export function mapToOptions(
  map: Record<string, string>,
): { value: string; label: string }[] {
  return Object.entries(map).map(([value, label]) => ({ value, label }))
}

export const SUBJECT_OPTIONS = mapToOptions(subjectMap)
export const GRADE_OPTIONS = mapToOptions(gradeMap)
export const SEMESTER_OPTIONS = mapToOptions(semesterMap)
export const STAGE_OPTIONS = mapToOptions(educationStageMap)
export const LEARNING_LEVEL_OPTIONS = mapToOptions(learningLevelMap)

/**
 * 将学员列表项展开为列表行（一科一行）
 * @param students 学员集合
 */
export function flattenStudentRows(
  students: StudentItem[],
): StudentListRow[] {
  return students.flatMap((student) => {
    if (student.subjectList.length === 0) {
      return [
        {
          id: `${student.id}-empty`,
          studentId: student.id,
          studentName: student.studentName,
          phone: student.phone,
          points: student.points,
          status: student.status,
          allowEditBasicInfo: student.allowEditBasicInfo,
          createTime: student.createTime,
          subject: '',
          textbookName: '',
          grade: '',
          semester: '',
          educationStage: '',
          learningGrade: '',
          learningForce: 0,
          teacherName: '',
          teacherId: '',
        },
      ]
    }
    return student.subjectList.map((detail) => ({
      id: detail.id,
      studentId: student.id,
      studentName: student.studentName,
      phone: student.phone,
      points: student.points,
      status: student.status,
      allowEditBasicInfo: student.allowEditBasicInfo,
      createTime: student.createTime,
      subject: detail.subject,
      textbookName: detail.textbookName,
      grade: detail.grade,
      semester: detail.semester,
      educationStage: detail.educationStage,
      learningGrade: detail.learningGrade,
      learningForce: detail.learningForce,
      teacherName: detail.teacherName,
      teacherId: detail.teacherId,
    }))
  })
}

/** 选项 value → label */
export function optionLabel(
  options: readonly { value: string; label: string }[],
  value: string,
): string {
  return options.find((item) => item.value === value)?.label ?? (value || '—')
}

/** 表单内单条科目详情 */
export type StudentDetailFormValues = {
  /** 关联记录 id；新建时为空 */
  id?: string
  /** 本地键，用于列表渲染 */
  key: string
  /** 科目 */
  subject: string
  /** 教材 id */
  textbookId: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 教育阶段 */
  educationStage: string
  /** 学习力等级 */
  learningGrade: string
  /** 学习力值 */
  learningForce: number
  /** 所属教师 id；空表示不绑定 */
  teacherId: string
}

/** 新建 / 编辑表单 */
export type StudentFormValues = {
  /** 学员名称 */
  studentName: string
  /** 手机号 */
  phone: string
  /** 点数 */
  points: number
  /** 登录密码；仅新建，可空默认 123456 */
  password: string
  /** 科目详情集合（至少一条） */
  details: StudentDetailFormValues[]
}

/**
 * 创建空的科目详情表单项
 * @param key 本地键
 */
export function createEmptyDetailForm(
  key = `d-${Date.now()}`,
): StudentDetailFormValues {
  return {
    key,
    subject: '',
    textbookId: '',
    grade: '',
    semester: '',
    educationStage: '',
    learningGrade: '',
    learningForce: 0,
    teacherId: '',
  }
}

export const EMPTY_STUDENT_FORM: StudentFormValues = {
  studentName: '',
  phone: '',
  points: 0,
  password: '',
  details: [createEmptyDetailForm('d-init')],
}
